/**
 * Shared API instance for all services
 * This ensures all API calls go through the same interceptors
 * which handle 401 errors and auth context updates
 */

import axios from 'axios';
import { showWarning } from './toastService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Store context reference (will be set by AppContent)
let authContext = null;

export const setAuthContext = (context) => {
    console.log('🔐 setAuthContext called with:', context ? 'context object' : 'null');
    authContext = context;
};

// Request interceptor to add token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
            error.config?.url?.includes('/auth/register');

        if (error.response?.status === 401) {
            // Don't show "Session expired" for login endpoint - let the login form handle it
            if (!isAuthEndpoint) {
                console.log('❌ 401 Error detected. authContext:', authContext ? 'exists' : 'null');

                // Update auth context (triggers re-renders)
                if (authContext) {
                    console.log('✅ Calling authContext.setUnauthorized()');
                    authContext.setUnauthorized();
                } else {
                    console.error('❌ authContext is null! Cannot logout.');
                }

                // Show user feedback
                showWarning('Session expired. Please login again.', {
                    autoClose: 2000
                });
            }
        } else if (error.response?.status === 429) {
            // Rate limit exceeded
            console.log('⚠️ Rate limit exceeded');

            // Don't show toast for auth endpoints - let the form handle it
            if (!isAuthEndpoint) {
                showWarning(error.response.data.message || 'Too many requests. Please try again later.', {
                    autoClose: 5000
                });
            }
        }
        return Promise.reject(error);
    }
);

export default api;
